import { siteTexts } from "../../../shared/i18n/siteTexts";
import type { ChallengeStatus } from "../types/challengeAdmin";
import type { DraftPair, DraftPlayer } from "../types/challengeDraft";
import { createPairId, normalizeCpf } from "./challengePairs";

export const SUPER8_ROUNDS_PER_GROUP = 7;

/**
 * Cada rodada define as 4 duplas (por posição 0-7 entre os 8 jogadores) e como
 * elas se enfrentam. Esta tabela é a única combinação (dentre as testadas)
 * em que todo jogador é parceiro de todos os outros exatamente uma vez E
 * enfrenta cada adversário exatamente 2 vezes no total — evitando que um
 * jogador acabe pegando o mesmo rival em várias rodadas.
 */
const SUPER8_ROUND_TEMPLATES: Array<{
  match1: [[number, number], [number, number]];
  match2: [[number, number], [number, number]];
}> = [
  { match1: [[0, 2], [1, 7]], match2: [[3, 5], [4, 6]] },
  { match1: [[0, 5], [1, 4]], match2: [[2, 3], [6, 7]] },
  { match1: [[0, 7], [3, 4]], match2: [[1, 2], [5, 6]] },
  { match1: [[0, 1], [3, 6]], match2: [[2, 7], [4, 5]] },
  { match1: [[0, 4], [2, 6]], match2: [[1, 5], [3, 7]] },
  { match1: [[0, 6], [5, 7]], match2: [[1, 3], [2, 4]] },
  { match1: [[0, 3], [2, 5]], match2: [[1, 6], [4, 7]] },
];

export function buildSuper8Schedule(players: DraftPlayer[]) {
  const list = players
    .map((player) => ({
      cpf: normalizeCpf(player.cpf),
      name:
        String(player.name || "").trim() ||
        siteTexts.challengeEngine.labels.player,
    }))
    .filter((player) => player.cpf.length >= 8);

  const uniqueCpfs = Array.from(new Set(list.map((player) => player.cpf)));
  if (uniqueCpfs.length !== 8) {
    return {
      ok: false as const,
      error: siteTexts.admin.validation.super8PlayersRequired,
    };
  }

  const nameByCpf = list.reduce<Record<string, string>>((acc, item) => {
    acc[item.cpf] = item.name;
    return acc;
  }, {});

  const seenPairs = new Set<string>();
  const pairs: DraftPair[] = [];
  const rounds: Array<
    Array<{
      dupla1Id: string;
      dupla2Id: string;
      score1: number | "";
      score2: number | "";
    }>
  > = [];

  const registerPair = (a: string, b: string) => {
    const id = createPairId(a, b);
    if (!seenPairs.has(id)) {
      seenPairs.add(id);
      const [cpf1, cpf2] = id.split("-");
      pairs.push({
        id,
        cpf1,
        cpf2,
        label: `${nameByCpf[cpf1] || siteTexts.challengeEngine.labels.player} / ${
          nameByCpf[cpf2] || siteTexts.challengeEngine.labels.player
        }`,
      });
    }
    return id;
  };

  for (const template of SUPER8_ROUND_TEMPLATES) {
    const buildMatch = (match: [[number, number], [number, number]]) => {
      const [duplaA, duplaB] = match;
      return {
        dupla1Id: registerPair(uniqueCpfs[duplaA[0]], uniqueCpfs[duplaA[1]]),
        dupla2Id: registerPair(uniqueCpfs[duplaB[0]], uniqueCpfs[duplaB[1]]),
        score1: "" as const,
        score2: "" as const,
      };
    };

    rounds.push([buildMatch(template.match1), buildMatch(template.match2)]);
  }

  return { ok: true as const, pairs, rounds };
}

export function resolveSuper8RoundGroup(
  roundIndex: number,
  totalRounds: number,
  isSuper8Challenge: boolean,
  participantsCount: number,
  status: ChallengeStatus = "pending",
): "A" | "B" | null {
  if (!isSuper8Challenge) return null;
  if (status === "pending") return null;
  if (
    Number.isFinite(participantsCount) &&
    participantsCount > 0 &&
    participantsCount <= 8
  ) {
    return null;
  }
  const isCombined = totalRounds === SUPER8_ROUNDS_PER_GROUP * 2;
  if (!isCombined) return null;
  const splitPoint = SUPER8_ROUNDS_PER_GROUP;
  return roundIndex < splitPoint ? "A" : "B";
}
